import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Search, CalendarX, MessageSquare, ListPlus, Check, X, Upload, Users, Layers } from "lucide-react";

const CATEGORIES = [
    { code: "very_good", label: "Shumë mirë", tone: "bg-sage text-white" },
    { code: "good", label: "Mirë", tone: "bg-seablue text-white" },
    { code: "neutral", label: "Neutral", tone: "bg-slate-400 text-white" },
    { code: "bad", label: "Keq", tone: "bg-orange-500 text-white" },
    { code: "very_bad", label: "Shumë keq", tone: "bg-terracotta text-white" },
    { code: "warning", label: "Vërejtje", tone: "bg-red-600 text-white" },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function AdministrataStudents() {
    const [classes, setClasses] = useState([]);
    const [students, setStudents] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [filterClass, setFilterClass] = useState("all");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(new Set());
    const [msg, setMsg] = useState(null);

    const refresh = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterClass !== "all") params.class_id = filterClass;
            if (search) params.search = search;
            const [cls, st, su] = await Promise.all([
                api.get("/administrata/classes"),
                api.get("/administrata/students", { params }),
                api.get("/subjects"),
            ]);
            setClasses(cls.data); setStudents(st.data); setSubjects(su.data);
        } finally { setLoading(false); }
    };

    // initial + filter change
    useEffect(() => {
        const t = setTimeout(refresh, search ? 300 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [filterClass, search]);

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };
    const toggleAll = () => {
        if (selected.size === students.length) setSelected(new Set());
        else setSelected(new Set(students.map((s) => s.id)));
    };

    const selectedArr = useMemo(() => students.filter((s) => selected.has(s.id)), [students, selected]);

    return (
        <Layout>
            <Link to="/administrata" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4" data-testid="admstudents-back">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>

            <div className="mb-5">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Administrata</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Nxënësit & Klasat</h1>
                <p className="mt-1 text-slate-600 text-sm">Kërko nxënësin, zgjidh disa dhe shto mungesa, komente ose detyra në bulk.</p>
            </div>

            {msg && (
                <div className={`mb-4 px-3 py-2 rounded-lg text-sm ${msg.ok ? "bg-sage/15 text-sage" : "bg-red-100 text-red-800"}`} data-testid="admstudents-msg">{msg.text}</div>
            )}

            {/* Class filter pills */}
            <div className="flex flex-wrap gap-2 mb-3" data-testid="admstudents-classes">
                <button onClick={() => setFilterClass("all")}
                    className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${filterClass === "all" ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200"}`}
                    data-testid="admstudents-class-all">
                    <Users className="w-3.5 h-3.5 inline mr-1" /> Të gjithë
                </button>
                {classes.map((c) => (
                    <button key={c.id} onClick={() => setFilterClass(c.id)}
                        className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${filterClass === c.id ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200"}`}
                        data-testid={`admstudents-class-${c.id}`}>
                        <Layers className="w-3.5 h-3.5 inline mr-1" /> {c.name} • {c.student_count}
                    </button>
                ))}
            </div>

            {/* Search + bulk actions bar */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kërko nxënës me emër…" className="pl-9" data-testid="admstudents-search" />
                </div>
                <div className="flex flex-wrap gap-2">
                    <BulkAbsenceDialog selected={selectedArr} subjects={subjects} onDone={(n) => { setMsg({ ok: true, text: `${n} mungesa u shtuan.` }); setSelected(new Set()); }} />
                    <BulkCommentDialog selected={selectedArr} subjects={subjects} onDone={(n) => { setMsg({ ok: true, text: `${n} komente u shtuan.` }); setSelected(new Set()); }} />
                    <BulkAssignmentDialog selected={selectedArr} subjects={subjects} onDone={(n) => { setMsg({ ok: true, text: `${n} detyra u krijuan.` }); setSelected(new Set()); }} />
                    <ImportCommentsDialog onDone={() => setMsg({ ok: true, text: "Importi i komenteve u krye." })} />
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admstudents-list">
                <div className="px-4 py-2.5 bg-bone/60 border-b border-slate-200 flex items-center gap-3">
                    <input type="checkbox" checked={students.length > 0 && selected.size === students.length} onChange={toggleAll}
                        className="h-4 w-4 accent-oxford" data-testid="admstudents-selectall" />
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-600">
                        {selected.size > 0 ? `${selected.size} të zgjedhur` : `${students.length} nxënës`}
                    </span>
                </div>
                {loading ? <div className="p-6 text-slate-500">Duke ngarkuar…</div> :
                 students.length === 0 ? <div className="p-8 text-center text-slate-500">Asnjë nxënës.</div> : (
                    <div className="divide-y divide-slate-200">
                        {students.map((s) => (
                            <div key={s.id} className="flex items-center gap-3 px-3 sm:px-6 py-3 hover:bg-bone/40 transition" data-testid={`admstudents-row-${s.id}`}>
                                <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSelect(s.id)}
                                    className="h-4 w-4 accent-oxford" data-testid={`admstudents-check-${s.id}`} />
                                <Link to={`/teacher/student/${s.id}`} className="flex items-center gap-3 flex-1 min-w-0" data-testid={`admstudents-open-${s.id}`}>
                                    <div className="w-10 h-10 rounded-xl bg-oxford text-white flex items-center justify-center font-bold text-sm shrink-0">
                                        {s.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-slate-900 truncate group-hover:text-oxford">{s.name}</div>
                                        <div className="text-[11px] text-slate-500 truncate">
                                            Viti {s.year} {s.class_name ? `• ${s.class_name}` : "• Pa klasë"}
                                        </div>
                                    </div>
                                </Link>
                                <Link to={`/teacher/student/${s.id}`} className="text-xs text-oxford font-semibold hover:underline shrink-0">
                                    Notat →
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
}

/* ========== Bulk Absence ========== */
const BulkAbsenceDialog = ({ selected, subjects, onDone }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ subject_id: "", date: today(), hours: 1, reason: "unexcused", comment: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            let ok = 0;
            for (const s of selected) {
                await api.post("/teacher/absence", { student_id: s.id, ...form });
                ok++;
            }
            setOpen(false); setForm({ subject_id: "", date: today(), hours: 1, reason: "unexcused", comment: "" });
            onDone && onDone(ok);
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setErr(""); }}>
            <DialogTrigger asChild>
                <Button size="sm" variant="outline" disabled={selected.length === 0} className="gap-1.5" data-testid="admstudents-bulk-absence-btn">
                    <CalendarX className="w-4 h-4" /> Mungesa ({selected.length})
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Shto mungesë për {selected.length} nxënës</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <select required value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })}
                        className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="bulk-absence-subject">
                        <option value="">— Zgjidh lëndën —</option>
                        {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="bulk-absence-date" />
                    <Input type="number" min={1} max={7} value={form.hours} onChange={(e) => setForm({ ...form, hours: parseInt(e.target.value) || 1 })} data-testid="bulk-absence-hours" />
                    <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                        className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="bulk-absence-reason">
                        <option value="unexcused">Pa arsye</option>
                        <option value="excused">Me arsye</option>
                    </select>
                    <Input placeholder="Koment (opsional)" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="bulk-absence-comment" />
                    {err && <div className="text-xs text-terracotta">{err}</div>}
                    <DialogFooter>
                        <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="bulk-absence-submit">
                            {busy ? "Duke ruajtur…" : "Ruaj për të gjithë"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

/* ========== Bulk Comment ========== */
const BulkCommentDialog = ({ selected, subjects, onDone }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ subject_id: "", category: "good", text: "", date: today() });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            let ok = 0;
            for (const s of selected) {
                await api.post("/teacher/comment", { student_id: s.id, ...form });
                ok++;
            }
            setOpen(false); setForm({ subject_id: "", category: "good", text: "", date: today() });
            onDone && onDone(ok);
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setErr(""); }}>
            <DialogTrigger asChild>
                <Button size="sm" variant="outline" disabled={selected.length === 0} className="gap-1.5" data-testid="admstudents-bulk-comment-btn">
                    <MessageSquare className="w-4 h-4" /> Koment ({selected.length})
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Shto koment për {selected.length} nxënës</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <select required value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })}
                        className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="bulk-comment-subject">
                        <option value="">— Zgjidh lëndën —</option>
                        {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <div className="flex flex-wrap gap-1.5">
                        {CATEGORIES.map((c) => (
                            <button type="button" key={c.code} onClick={() => setForm({ ...form, category: c.code })}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold transition border ${form.category === c.code ? c.tone + " border-transparent" : "bg-white border-slate-200 text-slate-700"}`}
                                data-testid={`bulk-comment-cat-${c.code}`}>
                                {c.label}
                            </button>
                        ))}
                    </div>
                    <textarea required value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Shkruaj komentin…"
                        rows={3} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="bulk-comment-text" />
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="bulk-comment-date" />
                    {err && <div className="text-xs text-terracotta">{err}</div>}
                    <DialogFooter>
                        <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="bulk-comment-submit">
                            {busy ? "Duke ruajtur…" : "Ruaj"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

/* ========== Bulk Assignment ========== */
const BulkAssignmentDialog = ({ selected, subjects, onDone }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ subject_id: "", title: "", instructions: "", deadline_days: 5 });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            let ok = 0;
            for (const s of selected) {
                await api.post("/teacher/assignment", {
                    student_id: s.id,
                    subject_id: form.subject_id || null,
                    title: form.title,
                    instructions: form.instructions,
                    deadline_days: Number(form.deadline_days) || 5,
                    resources: [],
                });
                ok++;
            }
            setOpen(false); setForm({ subject_id: "", title: "", instructions: "", deadline_days: 5 });
            onDone && onDone(ok);
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setErr(""); }}>
            <DialogTrigger asChild>
                <Button size="sm" disabled={selected.length === 0} className="bg-oxford hover:bg-oxfordhover text-white gap-1.5" data-testid="admstudents-bulk-assignment-btn">
                    <ListPlus className="w-4 h-4" /> Detyrë ({selected.length})
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Krijo detyrë për {selected.length} nxënës</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <Input placeholder="Titulli i detyrës" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="bulk-asg-title" />
                    <select value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })}
                        className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="bulk-asg-subject">
                        <option value="">— Pa lëndë —</option>
                        {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} placeholder="Udhëzime për nxënësin…"
                        rows={3} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="bulk-asg-instructions" />
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1">Afati (ditë)</label>
                        <Input type="number" min={1} max={30} value={form.deadline_days} onChange={(e) => setForm({ ...form, deadline_days: parseInt(e.target.value) || 5 })} data-testid="bulk-asg-deadline" />
                    </div>
                    {err && <div className="text-xs text-terracotta">{err}</div>}
                    <DialogFooter>
                        <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="bulk-asg-submit">
                            {busy ? "Duke krijuar…" : "Krijo për të gjithë"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

/* ========== Import Comments ========== */
const ImportCommentsDialog = ({ onDone }) => {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        if (!file) return;
        setBusy(true); setResult(null);
        try {
            const fd = new FormData(); fd.append("file", file);
            const r = await api.post("/admin/import/comments", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setResult(r.data);
            onDone && onDone();
        } catch (e2) { setResult({ error: formatApiError(e2.response?.data?.detail) || e2.message }); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setFile(null); setResult(null); } }}>
            <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1.5" data-testid="admstudents-import-comments-btn">
                    <Upload className="w-4 h-4" /> Import Komente
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Import CSV — Komente</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div className="text-xs text-slate-600">
                        <div className="font-bold uppercase tracking-widest text-slate-500 mb-1">Kolonat e pritura:</div>
                        <code className="block bg-slate-100 p-2 rounded text-xs font-mono">student_name,subject_name,category,text,date</code>
                    </div>
                    <Input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files[0])} required data-testid="admstudents-import-comments-file" />
                    {result && (
                        <div className="text-xs bg-bone/60 border border-slate-200 rounded-lg p-3">
                            {result.error ? <div className="text-terracotta font-bold">{result.error}</div> :
                                <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-sage" />
                                    <strong className="text-sage">{result.inserted}</strong> të shtuara nga {result.total} • <strong className="text-terracotta">{result.skipped?.length || 0}</strong> të kapërcyera
                                </div>
                            }
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="submit" disabled={busy || !file} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="admstudents-import-comments-submit">
                            {busy ? "Duke ngarkuar…" : "Ngarko CSV"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
